import React, { useState } from 'react';
import CitationTooltip from './CitationTooltip';
import { Citation } from './types';

interface InlineCitationProps {
  citationNumber: number;
  citation: Citation;
  onClick?: (citation: Citation) => void;
}

// Plan 159 §"Frontend rendering" — subtle source-aware tint on the
// citation marker. The shape of the marker is identical across sources so
// reading flow is preserved; the colour distinguishes provenance at a
// glance and the tooltip carries the full source label on hover.
//
// Legacy persisted matter-wide citations have no source_type — treat
// undefined as 'matter' so historical sessions render exactly as today.
const sourceClass = (citation: Citation): string => {
  const effective = citation.source_type ?? 'matter';
  switch (effective) {
    case 'workbench':
      // Muted emerald — matches the chip-strip workbench tint to
      // visually link the inline citation back to the source the user
      // attached/scoped on.
      return 'text-emerald-700 hover:text-emerald-800';
    case 'matter':
    default:
      // Slate (the existing info color) — unchanged from pre-159 so
      // legacy chat history renders byte-for-byte identical to today.
      return 'text-info hover:text-info';
  }
};

const InlineCitation: React.FC<InlineCitationProps> = ({
  citationNumber,
  citation,
  onClick
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(citation);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleClick();
    }
  };

  const effectiveType = citation.source_type ?? 'matter';

  return (
    <CitationTooltip citation={citation} visible={isHovered}>
      <button
        data-testid={`citation-${citationNumber}`}
        data-source-type={effectiveType}
        className={`${sourceClass(citation)} font-medium text-sm mx-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-action rounded inline`}
        style={{ display: 'inline', padding: 0, border: 'none', background: 'none', verticalAlign: 'baseline' }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={handleKeyDown}
        type="button"
      >
        [{citationNumber}]
      </button>
    </CitationTooltip>
  );
};

export default InlineCitation;
